import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import BaseModal from '@/components/modals/BaseModal';
import {
  addPrototypeFirmware,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';

const MAX_BYTES = 100 * 1024 * 1024;

type Props = {
  open: boolean;
  onClose: () => void;
  onUploaded: () => Promise<void> | void;
};

// Parent remounts this modal via `key={addModalOpen ? 'open' : 'closed'}`,
// so form state is cleared naturally by unmount on close rather than via a
// setState-in-effect cascade.
const AddPrototypeFirmwareModal: React.FC<Props> = ({
  open,
  onClose,
  onUploaded,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError('');
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setFileError('Firmware file must be a .zip file.');
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setFileError('Firmware file must be 100 MB or smaller.');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const trimmedName = name.trim();
  const trimmedVersion = version.trim();
  const canSubmit =
    !!file &&
    trimmedName.length > 0 &&
    trimmedName.length <= 200 &&
    trimmedVersion.length > 0 &&
    trimmedVersion.length <= 50 &&
    description.length <= 2000 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    setSubmitting(true);
    try {
      await addPrototypeFirmware(
        file,
        trimmedName,
        trimmedVersion,
        description.trim(),
      );
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'Firmware',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await onUploaded();
        } catch {
          // parent's refetch toast covers this
        }
      }
      setSubmitting(false);
      return;
    }
    showToast(
      `Uploaded ${trimmedName} v${trimmedVersion}.`,
      ToastType.SUCCESS,
    );
    try {
      await onUploaded();
    } catch {
      // onUploaded failures are surfaced by the parent's own refetch toast.
    }
    onClose();
    setSubmitting(false);
  };

  return (
    <BaseModal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Upload Prototype Firmware"
      submitButtonText=""
      onSubmit={() => {}}
      enableEnterKey={false}
      showCloseButton={!submitting}
      footerContent={
        <>
          <Button onClick={onClose} disabled={submitting} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            Upload
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="body2" gutterBottom>
            Select a firmware .zip (max 100 MB):
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            disabled={submitting}
          />
          {fileError && (
            <Typography variant="caption" color="error">
              {fileError}
            </Typography>
          )}
        </Box>
        <TextField
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 200))}
          error={name.length > 0 && trimmedName.length === 0}
          helperText={
            name.length > 0 && trimmedName.length === 0
              ? 'Name is required.'
              : ''
          }
          disabled={submitting}
          fullWidth
        />
        <TextField
          label="Version"
          required
          value={version}
          onChange={(e) => setVersion(e.target.value.slice(0, 50))}
          error={version.length > 0 && trimmedVersion.length === 0}
          helperText={
            version.length > 0 && trimmedVersion.length === 0
              ? 'Version is required.'
              : ''
          }
          disabled={submitting}
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
          multiline
          minRows={3}
          maxRows={8}
          disabled={submitting}
          fullWidth
        />
        {submitting && (
          <Box>
            <Typography variant="caption">Uploading…</Typography>
            <LinearProgress />
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default AddPrototypeFirmwareModal;
